import { useNavigate } from 'react-router-dom';
import './Pages.css';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="page-wrap" style={{ alignItems: 'flex-start', padding: '40px 20px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', width: '100%' }}>

        <div style={{ marginBottom: 32 }}>
          <div
            className="logo"
            style={{ fontSize: 22, marginBottom: 16, cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <span className="logo-r">R</span>esumify
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Privacy Policy</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            Last updated: 1 April 2026
          </p>
        </div>

        <div className="terms-body">

          <section>
            <h2>1. Introduction</h2>
            <p>
              Resumify ("we", "us", "our") is committed to protecting your personal information.
              This Privacy Policy explains how we collect, use, store, and protect your data
              when you use our service at <a href="https://www.resumify.co.za">www.resumify.co.za</a>.
            </p>
            <p>
              This policy is in accordance with the Protection of Personal Information Act
              (POPIA) of South Africa. By using Resumify, you consent to the practices
              described in this policy.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            <p>We collect the following information when you use Resumify:</p>
            <ul>
              <li>
                <strong>Email address</strong> — collected during the email verification step
                to confirm your identity and manage your free tier eligibility
              </li>
              <li>
                <strong>CV content</strong> — the CV you upload is temporarily processed
                to generate your tailored CV and is not permanently stored
              </li>
              <li>
                <strong>Job description content</strong> — uploaded or fetched job descriptions
                are temporarily processed and are not permanently stored
              </li>
              <li>
                <strong>Usage data</strong> — we store your email address and a count of
                how many CVs you have tailored, to manage free tier access
              </li>
              <li>
                <strong>Payment information</strong> — payment details are handled entirely
                by PayFast. We do not collect or store your card details
              </li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use your information solely for the following purposes:</p>
            <ul>
              <li>To verify your email address before processing your CV</li>
              <li>To process your CV and job description through our AI pipeline</li>
              <li>To manage your free tier eligibility and usage count</li>
              <li>To process payments via PayFast</li>
              <li>To respond to support queries sent to support@resumify.co.za</li>
              <li>To improve our service based on usage patterns</li>
            </ul>
            <p>
              We do not use your information for marketing purposes, and we do not
              send unsolicited emails.
            </p>
          </section>

          <section>
            <h2>4. How We Store Your Information</h2>
            <p>
              <strong>CV and job description files</strong> are processed in temporary memory
              and deleted immediately after your tailored CV is generated. They are never
              written to permanent storage.
            </p>
            <p>
              <strong>Your email address and usage count</strong> are stored in a secure
              PostgreSQL database hosted on Railway's infrastructure in the EU (Europe West).
            </p>
            <p>
              <strong>Verification codes</strong> are stored temporarily in memory for
              10 minutes and automatically expire.
            </p>
          </section>

          <section>
            <h2>5. Sharing Your Information</h2>
            <p>
              We do not sell, trade, or rent your personal information to third parties.
              We share information only in the following limited circumstances:
            </p>
            <ul>
              <li>
                <strong>PayFast</strong> — your name and email address are shared with
                PayFast to process your payment. PayFast's privacy policy applies to
                information they receive
              </li>
              <li>
                <strong>Anthropic</strong> — your CV and job description content is sent
                to Anthropic's Claude AI API for processing. Anthropic's privacy policy
                and data processing terms apply
              </li>
              <li>
                <strong>Legal requirements</strong> — we may disclose your information
                if required by law or in response to valid legal processes
              </li>
            </ul>
          </section>

          <section>
            <h2>6. Your Rights Under POPIA</h2>
            <p>
              As a South African resident, you have the following rights regarding
              your personal information:
            </p>
            <ul>
              <li><strong>Right to access</strong> — you may request a copy of the personal information we hold about you</li>
              <li><strong>Right to correction</strong> — you may request that we correct inaccurate information</li>
              <li><strong>Right to deletion</strong> — you may request that we delete your personal information from our systems</li>
              <li><strong>Right to object</strong> — you may object to the processing of your personal information</li>
              <li><strong>Right to complain</strong> — you may lodge a complaint with the Information Regulator of South Africa</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:support@resumify.co.za">support@resumify.co.za</a>.
              We will respond within 30 days.
            </p>
          </section>

          <section>
            <h2>7. Cookies</h2>
            <p>
              Resumify does not use tracking cookies or advertising cookies.
              Our application may use essential browser storage to maintain your
              session state during a visit, which is deleted when you close the browser.
            </p>
          </section>

          <section>
            <h2>8. Third Party Services</h2>
            <p>Our service relies on the following third party providers:</p>
            <ul>
              <li><strong>Anthropic (Claude AI)</strong> — AI processing of CV and job description content</li>
              <li><strong>PayFast</strong> — payment processing</li>
              <li><strong>Railway</strong> — backend hosting and database</li>
              <li><strong>Vercel</strong> — frontend hosting</li>
              <li><strong>Resend</strong> — transactional email delivery</li>
            </ul>
            <p>
              Each of these providers has their own privacy policy. We encourage you
              to review their policies if you have concerns about how they handle data.
            </p>
          </section>

          <section>
            <h2>9. Data Security</h2>
            <p>
              We implement appropriate technical and organisational measures to protect
              your personal information against unauthorised access, alteration, disclosure,
              or destruction. These include encrypted connections (HTTPS), secure database
              access, and environment variable protection for API keys.
            </p>
            <p>
              However, no method of transmission over the internet is 100% secure.
              While we strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2>10. Children's Privacy</h2>
            <p>
              Resumify is not intended for use by anyone under the age of 18.
              We do not knowingly collect personal information from children.
              If you believe a child has provided us with personal information,
              please contact us and we will delete it immediately.
            </p>
          </section>

          <section>
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be
              posted on this page with an updated date. Continued use of the service
              after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2>12. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or how we handle
              your personal information, please contact us:
            </p>
            <ul>
              <li>Email: <a href="mailto:support@resumify.co.za">support@resumify.co.za</a></li>
              <li>Website: <a href="https://www.resumify.co.za">www.resumify.co.za</a></li>
            </ul>
            <p>
              You may also contact the Information Regulator of South Africa
              if you believe your rights under POPIA have been violated:
            </p>
            <ul>
              <li>Website: <a href="https://www.inforegulator.org.za" target="_blank" rel="noreferrer">www.inforegulator.org.za</a></li>
              <li>Email: inforeg@justice.gov.za</li>
            </ul>
          </section>

        </div>

        <div style={{ marginTop: 40, paddingTop: 24, borderTop: '1px solid var(--border)' }}>
          <button className="page-btn-secondary" onClick={() => navigate('/')}>
            ← Back to Resumify
          </button>
        </div>

      </div>
    </div>
  );
}
